'use client'

import React from 'react'

import { Box, Button, HStack, Text } from '@chakra-ui/react'

import { tokenPrefix } from '../../theme/tokenPrefix'
import type { ColorScheme } from '../../types'
import {
  mediaToggleButtonBaseStyles,
  mediaToggleSpinnerAnimation,
} from './MediaToggle.styles'

export type MediaToggleProps = {
  /** Theming contract shared by dark components. Defaults to 'dark'. */
  colorScheme?: ColorScheme
  /** Live button filled with accent color */
  isLiveActive?: boolean
  /** Clip button filled with accent color (ignored if isClipLoading is true) */
  isClipActive?: boolean
  /** Clip button shows loading spinner and is disabled */
  isClipLoading?: boolean
  /** Label for clip button when not loading (default: 'Clip available') */
  clipLabel?: string
  /** Label for the Live button (default: 'Live') */
  liveLabel?: string
  /** Label for the clip button while loading (default: 'Retrieving clip…') */
  retrievingLabel?: string
  /** Extended button filled with accent color */
  isExtendedActive?: boolean
  /** Show the Extended (forensic clip) button */
  showExtended?: boolean
  /** Label for the Extended button (default: 'Extended') */
  extendedLabel?: string
  onLiveClick: () => void
  onClipClick: () => void
  onExtendedClick?: () => void
}

/**
 * Live / Clip / Extended segmented media-source toggle. Pixel-parity port of
 * the web-app's Chakra v1 MediaToggle (ENG-31544) — visuals to be reconciled
 * when Figma designs the rebrand version.
 */
export const MediaToggle = ({
  colorScheme = 'dark',
  isLiveActive = false,
  isClipActive = false,
  isClipLoading = false,
  clipLabel = 'Clip available',
  liveLabel = 'Live',
  retrievingLabel = 'Retrieving clip…',
  isExtendedActive = false,
  showExtended = false,
  extendedLabel = 'Extended',
  onLiveClick,
  onClipClick,
  onExtendedClick,
}: MediaToggleProps) => {
  const prefix = tokenPrefix(colorScheme)

  const accent = `${prefix}interactive.primary`
  const accentText = `${prefix}text.on-accent`

  // Accent opacity variants — the raw RGB of interactive.primary per scheme
  // (teal light / signalYellow dark), carried from the legacy v1 component
  // because the token system has no alpha variants of accent yet.
  const accentBase = colorScheme === 'light' ? '20, 184, 166' : '221, 254, 85'
  const accentAlpha = (opacity: number) => `rgba(${accentBase}, ${opacity})`

  const isClipReady = !isClipLoading && !isClipActive
  const glowShadow = `inset 0 0 0 1px ${accentAlpha(0.35)}`

  return (
    <HStack
      bg={`${prefix}bg.input`}
      border='1px solid'
      borderColor={`${prefix}border.default`}
      borderRadius='0'
      gap='2px'
      p='2px'
      w='min'
    >
      <Button
        {...mediaToggleButtonBaseStyles}
        _hover={
          isLiveActive
            ? { bg: accent, color: accentText }
            : { color: `${prefix}text.secondary`, bg: accentAlpha(0.08) }
        }
        bg={isLiveActive ? accent : 'transparent'}
        color={isLiveActive ? accentText : `${prefix}text.tertiary`}
        data-testid='expand-livestream-button'
        onClick={onLiveClick}
      >
        {liveLabel}
      </Button>

      <Button
        {...mediaToggleButtonBaseStyles}
        _disabled={{ opacity: 1, cursor: 'default' }}
        _hover={
          isClipLoading
            ? { bg: accentAlpha(0.08), color: accent }
            : {
                bg: isClipActive ? accent : accentAlpha(0.12),
                color: isClipActive ? accentText : accent,
                boxShadow: isClipReady ? glowShadow : undefined,
              }
        }
        bg={
          isClipLoading
            ? accentAlpha(0.08)
            : isClipActive
            ? accent
            : accentAlpha(0.12)
        }
        boxShadow={isClipReady ? glowShadow : undefined}
        color={isClipActive ? accentText : accent}
        cursor={isClipLoading ? 'default' : 'pointer'}
        data-testid={
          isClipLoading ? 'retrieving-clip-button' : 'clip-available-button'
        }
        disabled={isClipLoading}
        onClick={isClipLoading ? undefined : onClipClick}
      >
        <HStack gap='6px'>
          {isClipLoading && (
            <Box
              border='1.5px solid'
              borderColor={accentAlpha(0.25)}
              borderRadius='50%'
              borderTopColor={accent}
              css={mediaToggleSpinnerAnimation}
              flexShrink={0}
              h='10px'
              w='10px'
            />
          )}
          <Text as='span'>{isClipLoading ? retrievingLabel : clipLabel}</Text>
        </HStack>
      </Button>

      {showExtended && (
        <Button
          {...mediaToggleButtonBaseStyles}
          _hover={
            isExtendedActive
              ? { bg: accent, color: accentText }
              : { color: `${prefix}text.secondary`, bg: accentAlpha(0.08) }
          }
          bg={isExtendedActive ? accent : 'transparent'}
          color={isExtendedActive ? accentText : `${prefix}text.tertiary`}
          data-testid='extended-clip-button'
          onClick={onExtendedClick}
        >
          {extendedLabel}
        </Button>
      )}
    </HStack>
  )
}
